import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Phone,
  MapPin,
  Clock,
  StickyNote,
  Camera,
  ChevronDown,
  MessageCircle,
  Navigation,
} from "lucide-react";
import { toast } from "sonner";
import { useProspects } from "../../context/ProspectsContext";
import { PRODUCT_TYPES, PROSPECT_STATUS } from "../../constants/config";
import { StatusBadge } from "../../components/sales/StatusBadge";

const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const formatPrice = (n) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n);

export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getById, updateStatus } = useProspects();
  const prospect = getById(id);
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  if (!prospect) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-honda-muted">Prospek tidak ditemukan.</p>
        <button onClick={() => navigate("/sales/prospects")} className="mt-3 honda-btn-outline text-sm">
          Kembali
        </button>
      </div>
    );
  }

  const product = PRODUCT_TYPES.find((p) => p.id === (prospect.interested_product || prospect.interestedProduct));

  const handleChangeStatus = (newStatus) => {
    updateStatus(prospect.id, newStatus);
    setShowStatusMenu(false);
    toast.success(`Status diperbarui ke ${PROSPECT_STATUS[newStatus].label}`);
  };

  return (
    <div data-testid="prospect-detail-page" className="pb-4">
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-honda-red to-[#9F140E] text-white pt-8 pb-12 px-5">
        <button
          data-testid="detail-back-button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center hover:bg-white/25"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="mt-4">
          <StatusBadge status={prospect.status} size="md" />
          <h1 className="mt-3 font-heading font-extrabold text-2xl leading-tight">
            {prospect.name}
          </h1>
          <p className="text-xs text-white/85 mt-1 flex items-center gap-1">
            <Clock size={12} /> Dikunjungi {formatDateTime(prospect.visited_at || prospect.visitedAt)}
          </p>
        </div>
      </div>

      {/* Quick actions card overlap */}
      <div className="px-5 -mt-10">
        <div className="honda-card p-3 flex items-center justify-around shadow-lg">
          <a
            href={`tel:${prospect.phone}`}
            data-testid="detail-call-button"
            className="flex flex-col items-center gap-1 px-3 py-1.5 hover:bg-honda-bg rounded-lg"
          >
            <Phone size={18} className="text-honda-red" />
            <span className="text-[10px] font-semibold text-honda-ink">Telepon</span>
          </a>
          <a
            href={`https://wa.me/${prospect.phone.replace(/\D/g, "")}`}
            target="_blank"
            rel="noreferrer"
            data-testid="detail-wa-button"
            className="flex flex-col items-center gap-1 px-3 py-1.5 hover:bg-honda-bg rounded-lg"
          >
            <MessageCircle size={18} className="text-green-600" />
            <span className="text-[10px] font-semibold text-honda-ink">WhatsApp</span>
          </a>
          <a
            href={`https://maps.google.com/?q=${prospect.coord.lat},${prospect.coord.lng}`}
            target="_blank"
            rel="noreferrer"
            data-testid="detail-direction-button"
            className="flex flex-col items-center gap-1 px-3 py-1.5 hover:bg-honda-bg rounded-lg"
          >
            <Navigation size={18} className="text-blue-600" />
            <span className="text-[10px] font-semibold text-honda-ink">Navigasi</span>
          </a>
        </div>
      </div>

      {/* Details */}
      <div className="px-5 mt-5 space-y-3">
        {/* Contact */}
        <div className="honda-card p-4">
          <p className="honda-label">Informasi Kontak</p>
          <div className="mt-2 space-y-2.5">
            <div className="flex items-start gap-2.5">
              <Phone size={14} className="text-honda-subtle mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-honda-subtle uppercase tracking-wider">Telepon</p>
                <p className="text-sm text-honda-ink">{prospect.phone}</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <MapPin size={14} className="text-honda-subtle mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-honda-subtle uppercase tracking-wider">Alamat</p>
                <p className="text-sm text-honda-ink">{prospect.address}</p>
                <p className="text-[11px] text-honda-muted mt-0.5">
                  {prospect.coord.lat.toFixed(4)}, {prospect.coord.lng.toFixed(4)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Product interest */}
        {product && (
          <div className="honda-card p-4">
            <p className="honda-label">Produk Diminati</p>
            <div className="mt-2 flex items-center justify-between">
              <div>
                <p className="font-heading font-bold text-base text-honda-ink">{product.name}</p>
                <p className="text-[11px] text-honda-muted">{product.category}</p>
              </div>
              <p className="font-heading font-bold text-sm text-honda-red">
                {formatPrice(product.price)}
              </p>
            </div>
          </div>
        )}

        {/* Notes */}
        {prospect.notes && (
          <div className="honda-card p-4">
            <div className="flex items-center gap-1.5">
              <StickyNote size={14} className="text-honda-red" />
              <p className="honda-label">Catatan Kunjungan</p>
            </div>
            <p className="mt-2 text-sm text-honda-ink leading-relaxed">{prospect.notes}</p>
          </div>
        )}

        {/* Photo evidence */}
        <div className="honda-card p-4">
          <div className="flex items-center gap-1.5">
            <Camera size={14} className="text-honda-red" />
            <p className="honda-label">Foto Bukti Kunjungan</p>
          </div>
          {prospect.photo ? (
            <img
              src={prospect.photo}
              alt="Bukti"
              className="mt-3 w-full max-h-60 object-contain rounded-lg border border-honda-border bg-honda-bg"
              data-testid="prospect-photo"
            />
          ) : (
            <div className="mt-3 w-full h-32 rounded-lg border border-dashed border-honda-border bg-honda-bg flex items-center justify-center">
              <p className="text-xs text-honda-subtle">Tidak ada foto</p>
            </div>
          )}
        </div>

        {/* Update status */}
        <div className="relative">
          <button
            data-testid="detail-change-status-button"
            onClick={() => setShowStatusMenu((v) => !v)}
            className="w-full honda-btn-primary py-3 flex items-center justify-center gap-2"
          >
            Update Status <ChevronDown size={16} />
          </button>
          {showStatusMenu && (
            <div className="absolute bottom-full mb-2 left-0 right-0 honda-card overflow-hidden shadow-lg z-10">
              {Object.entries(PROSPECT_STATUS).map(([key, val]) => (
                <button
                  key={key}
                  data-testid={`status-option-${key}`}
                  onClick={() => handleChangeStatus(key)}
                  className="w-full px-4 py-3 text-left hover:bg-honda-bg transition-colors flex items-center justify-between border-b border-honda-border last:border-0"
                >
                  <span className="text-sm text-honda-ink">{val.label}</span>
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: val.color }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
